import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressStepNavigation from '../../components/ui/ProgressStepNavigation';
import FormProgressIndicator from '../../components/ui/FormProgressIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import DocumentTypeSelector from './components/DocumentTypeSelector';
import FileUploadZone from './components/FileUploadZone';
import DocumentPreview from './components/DocumentPreview';
import UploadValidation from './components/UploadValidation';
import axios from "axios";

const IdentityDocumentUpload = () => {
  const navigate = useNavigate();
  
  // Form state
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [validationState, setValidationState] = useState('idle');
  const [validationMessage, setValidationMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Progress indicator state
  const [showProgress, setShowProgress] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState('idle');
  const [progressMessage, setProgressMessage] = useState('');

  const [information, setInformation] = useState(null);

  // Form validation
  const isFormValid = selectedDocumentType && uploadedFile && validationState === 'success';

  useEffect(() => {
    let information = localStorage.getItem("contactInformation");
    if (!information) {
      navigate('/');
    } else {
      let informationParsed = JSON.parse(information);
      if (!informationParsed.email || !informationParsed.phoneNumber || !informationParsed.contactDateTime) {
        navigate('/');
        return;
      }
      setInformation(information);
    }

  }, []);

  useEffect(() => {
    // Simulate document validation when file is uploaded
    if (uploadedFile) {
      setValidationState('processing');
      setValidationMessage('Validation du document en cours...');
      
      // Simulate validation process
      const validationTimer = setTimeout(() => {
        setValidationState('success');
        setValidationMessage('Document validé avec succès. Toutes les informations sont lisibles.');
      }, 2000);

      return () => clearTimeout(validationTimer);
    } else {
      setValidationState('idle');
      setValidationMessage('');
    }
  }, [uploadedFile]);

  const handleFileSelect = (file, result) => {
    if (result.type === 'error') {
      setValidationState('error');
      setValidationMessage(result.message);
      setUploadedFile(null);
      return;
    }

    if (file) {
      // Show upload progress
      setShowProgress(true);
      setProgressStatus('uploading');
      setProgressMessage('Téléchargement du document...');
      setUploadProgress(0);

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            setProgressStatus('processing');
            setProgressMessage('Traitement et validation du document...');
            
            setTimeout(() => {
              setShowProgress(false);
              setUploadedFile(file);
            }, 1500);
            
            return 100;
          }
          return prev + 10;
        });
      }, 200);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setValidationState('idle');
    setValidationMessage('');
  };

  const handleReplaceFile = () => {
    handleRemoveFile();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      setValidationState('error');
      setValidationMessage('Veuillez sélectionner un type de document et télécharger un fichier valide.');
      return;
    }

    setIsSubmitting(true);
    setShowProgress(true);
    setProgressStatus('processing');
    setProgressMessage('Soumission de votre document...');
    setUploadProgress(0);

    let progress = 0;
    const fakeProgressInterval = setInterval(() => {
      progress = Math.min(progress + 1, 90); // on bloque à 90% max
      setUploadProgress(progress);
    }, 50);

    try {
      const parsedInfo = JSON.parse(localStorage.getItem('contactInformation') || '{}');

      const formData = new FormData();
      formData.append('files', uploadedFile);
      formData.append('email', parsedInfo.email || '');
      formData.append('phoneNumber', parsedInfo.phoneNumber || '');
      formData.append('otp', parsedInfo.otp || '');
      formData.append('date', parsedInfo.contactDateTime || '');

      const response = await axios.post(`${import.meta.env.VITE_API_BACK}/kyc`, formData);

      if (response.status !== 200) throw new Error('Erreur lors de l’envoi du document');

      clearInterval(fakeProgressInterval);
      setUploadProgress(100);
      setProgressStatus('success');
      setProgressMessage('Document soumis avec succès !');

      setTimeout(() => {
        navigate('/submission');
      }, 1500);
    } catch (err) {
      clearInterval(fakeProgressInterval);
      console.error(err);
      setProgressStatus('error');
      setProgressMessage('Échec de la soumission. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedDocumentType('');
    setUploadedFile(null);
    setValidationState('idle');
    setValidationMessage('');
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressStepNavigation />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Page Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto shadow-elevation-2">
              <Icon name="Upload" size={32} color="white" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-text-primary font-inter">
                Téléchargez votre document d'identité officielle pour commencer le processus de vérification.
              </h1>
              <p className="text-lg text-text-secondary font-inter max-w-2xl mx-auto">
                Assurez-vous que le document est lisible et sa date de validité à jour.
              </p>
            </div>
          </div>

          {/* Main Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="bg-surface rounded-xl shadow-elevation-2 p-6 lg:p-8 space-y-8">
              
              {/* Document Type Selection */}
              <DocumentTypeSelector
                selectedType={selectedDocumentType}
                onTypeChange={setSelectedDocumentType}
              />

              {/* File Upload Section */}
              <div className="space-y-6">
                <div className="border-t border-border-light pt-8">
                  <h3 className="text-lg font-semibold text-text-primary font-inter mb-4">
                    Téléchargement du document
                  </h3>
                  
                  {!uploadedFile ? (
                    <FileUploadZone
                      onFileSelect={handleFileSelect}
                      acceptedFormats=".jpg,.jpeg,.png,.pdf"
                      maxSize={10}
                    />
                  ) : (
                    <DocumentPreview
                      file={uploadedFile}
                      onRemove={handleRemoveFile}
                      onReplace={handleReplaceFile}
                    />
                  )}
                </div>


              </div>
            </div>

            {/* Form Actions */}
            <div className="bg-surface rounded-xl shadow-elevation-2 p-6 lg:p-8">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                
                {/* Form Status */}
                <div className="flex items-center space-x-4 text-sm text-text-secondary font-inter">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={selectedDocumentType ? "CheckCircle" : "Circle"} 
                      size={16} 
                      color={selectedDocumentType ? "#10B981" : "#9CA3AF"} 
                    />
                    <span>Type de document</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={uploadedFile ? "CheckCircle" : "Circle"} 
                      size={16} 
                      color={uploadedFile ? "#10B981" : "#9CA3AF"} 
                    />
                    <span>Document téléchargé</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={validationState === 'success' ? "CheckCircle" : "Circle"} 
                      size={16} 
                      color={validationState === 'success' ? "#10B981" : "#9CA3AF"} 
                    />
                    <span>Validation</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-3">
                  <Button
                    variant="ghost"
                    onClick={handleReset}
                    disabled={isSubmitting}
                    iconName="RotateCcw"
                  >
                    Réinitialiser
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!isFormValid || isSubmitting}
                    loading={isSubmitting}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    {isSubmitting ? 'Soumission...' : 'Continuer'}
                  </Button>
                </div>
              </div>

              {/* Help Text */}
              <div className="mt-6 pt-6 border-t border-border-light">
                <div className="flex items-start space-x-3">
                  <Icon name="HelpCircle" size={20} color="#64748B" className="flex-shrink-0 mt-0.5" />
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-text-primary font-inter">
                      Besoin d'aide ?
                    </h4>
                    <p className="text-sm text-text-secondary font-inter">
                      Si vous rencontrez des difficultés pour télécharger votre document, 
                      assurez-vous qu'il respecte les formats acceptés (JPG, PNG, PDF) et 
                      que sa taille ne dépasse pas 10MB. Le document doit être lisible et bien éclairé.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>

      {/* Progress Indicator Modal */}
      <FormProgressIndicator
        isVisible={showProgress}
        progress={uploadProgress}
        status={progressStatus}
        message={progressMessage}
        fileName={uploadedFile?.name}
        fileSize={uploadedFile ? `${(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB` : ''}
        onCancel={() => {
          setShowProgress(false);
          setIsSubmitting(false);
        }}
        onRetry={() => {
          if (progressStatus === 'error') {
            handleSubmit({ preventDefault: () => {} });
          }
        }}
      />
    </div>
  );
};

export default IdentityDocumentUpload;